// src/components/Hero.js
import React, { useRef, useState, useEffect } from 'react';
import {
    motion,
    AnimatePresence,
    useTransform,
    useMotionValue,
    useSpring,
} from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

import ahmadabdallaLogo from '../assets/images/new/herologo/ahmad abdalla.svg';
import bioageLogo       from '../assets/images/new/herologo/bioage.svg';
import ericfavreLogo    from '../assets/images/new/herologo/eric favre.svg';
import florabelleLogo   from '../assets/images/new/herologo/flroabelle.svg';
import greenlabLogo     from '../assets/images/new/herologo/greenlab.svg';
import mainLogo         from '../assets/images/new/logo.png';

const containerVariants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.4,
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const Hero = () => {
    const { language } = useLanguage();
    const [isHovered, setIsHovered] = useState(false);
    const [currentCompany, setCurrentCompany] = useState(0);
    const containerRef = useRef(null);

    const companies = [
        { name: 'ahmadabdalla', logo: ahmadabdallaLogo },
        { name: 'bioage',       logo: bioageLogo },
        { name: 'ericfavre',    logo: ericfavreLogo },
        { name: 'florabelle',   logo: florabelleLogo },
        { name: 'greenlab',     logo: greenlabLogo },
    ];

    useEffect(() => {
        const timer = setInterval(
            () => setCurrentCompany((prev) => (prev + 1) % companies.length),
            3000
        );
        return () => clearInterval(timer);
    }, [companies.length]);

    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                mouseX.set((x / rect.width - 0.5) * 20);
                mouseY.set((y / rect.height - 0.5) * 20);
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [mouseX, mouseY]);

    const smoothMouseX = useSpring(mouseX, { stiffness: 70, damping: 15 });
    const smoothMouseY = useSpring(mouseY, { stiffness: 70, damping: 15 });

    const copy = {
        en: { title1: 'Short steps.', title2: 'Long Vision', trusted: 'your Brand first partner' },
        ar: { title1: ' .خطوات قصيرة', title2: 'رؤية طويلة', trusted: 'شريكك الأول لعلامتك التجارية' },
    }[language];

    return (
        <section
            id="home"
            className="relative flex flex-col items-center bg-light justify-center pt-2 min-h-screen md:pl-48"
            ref={containerRef}
            style={{ perspective: '1000px' }}
        >
            {[ 'top-8 left-8 border-t border-l',
                'top-8 right-8 border-t border-r',
                'bottom-8 left-8 border-b border-l',
                'bottom-8 right-8 border-b border-r'
            ].map((pos, i) => (
                <div key={i} className={`absolute ${pos} w-6 h-6 border-gray-200`} />
            ))}

            <motion.div
                className="relative z-10 flex flex-col items-center justify-center w-full max-w-7xl px-6"
                style={{
                    x: useTransform(smoothMouseX, [-10, 10], [10, -10]),
                    y: useTransform(smoothMouseY, [-10, 10], [10, -10]),
                    transformStyle: 'preserve-3d',
                }}
            >
                <motion.div
                    // --- FINAL FIX IS HERE ---
                    // Increased the bottom margin for better spacing between logo and title.
                    // Old: mb-4
                    // New: mb-8
                    className="w-40 h-40 md:w-40 md:h-40  mt-12 mb-12"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}

                >
                    <img
                        src={mainLogo}
                        alt="Agency Logo"
                        className="w-full h-full object-contain"
                    />
                </motion.div>

                <motion.h1
                    className="text-6xl md:text-7xl lg:text-8xl font-greycliff font-bold text-gray-800 mb-6 leading-tight text-center"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.span className="block font-greycliff font-normal text-gray-700" variants={itemVariants}>
                        {copy.title1}
                    </motion.span>
                    <motion.span
                        className="block mt-1 md:mt-2"
                        variants={itemVariants}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        animate={{ color: isHovered ? '#28826F' : '#1f2937' }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                    >
                        {copy.title2}
                    </motion.span>
                </motion.h1>

                <motion.div
                    className="w-full max-w-7xl mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.0 }}
                >
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5 text-center">
                        {copy.trusted}
                    </div>

                    <div className="flex justify-center">
                        <div className="flex justify-center items-center h-auto min-h-[80px] w-full relative overflow-visible -mt-12">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={currentCompany}
                                    src={companies[currentCompany].logo}
                                    alt={companies[currentCompany].name + ' logo'}
                                    className="h-48 md:h-72 max-w-[700px] object-contain"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -16 }}
                                    transition={{ duration: 0.6, ease: 'easeInOut' }}
                                />
                            </AnimatePresence>
                            <motion.div
                                key={currentCompany}
                                className="absolute bottom-0 left-0 right-0 h-0.5"
                                style={{ transformOrigin: 'left' }}
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 3, ease: 'linear' }}
                            />
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;